import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Mail } from "lucide-react";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { AdminStats } from "@/components/admin/AdminStats";
import { ReservationsTable, STATUS } from "@/components/admin/ReservationsTable";
import { CampaignPanel } from "@/components/admin/CampaignPanel";
import { CapacityCard } from "@/components/admin/CapacityCard";
import { ClubPanel } from "@/components/admin/ClubPanel";
import { ProgramsPanel } from "@/components/admin/ProgramsPanel";
import { PartnersPanel } from "@/components/admin/PartnersPanel";
import { me, logout, fetchReservations, fetchStats, setStatus, sendTestEmail, formatApiError } from "@/lib/adminApi";
import { usePageMeta } from "@/lib/usePageMeta";

export default function Admin() {
  usePageMeta({ title: "Admin — CARSPA CLUB", description: "Administrace CARSPA CLUB.", path: "/admin", noindex: true });
  const [user, setUser] = useState(null); // null = checking, false = anonymous
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const { code: scanParam } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(scanParam ? "club" : "reservations");
  const [scanCode, setScanCode] = useState(scanParam || "");
  const [loading, setLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const testEmail = async () => {
    setSendingTest(true);
    try {
      const { to } = await sendTestEmail();
      toast.success(`Testovací e-mail odeslán na ${to}.`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSendingTest(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([fetchReservations(), fetchStats()]);
      setItems(r);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) setUser(false);
      else toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    me().then(setUser).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const changeStatus = async (id, status) => {
    let paid;
    if (status === "done") {
      const v = window.prompt("Skutečně uhrazená částka za návštěvu (Kč):", String(items.find((r) => r.id === id)?.total_price ?? ""));
      if (v === null) return;
      paid = Number(v.replace(/\s/g, "")) || 0;
    }
    try {
      const updated = await setStatus(id, status, paid);
      setItems((list) => list.map((r) => (r.id === id ? { ...r, status: updated.status, paid_amount: updated.paid_amount } : r)));
      setStats(await fetchStats());
      toast.success(`Stav změněn na „${STATUS[status].label}“.`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  if (user === null) {
    return <div className="min-h-screen bg-brand-bg text-brand-muted flex items-center justify-center">Načítám...</div>;
  }
  if (!user) return <AdminLogin onLogin={setUser} />;

  const visible = filter === "all" ? items : items.filter((r) => r.status === filter);

  return (
    <div className="min-h-screen bg-brand-bg text-white" data-testid="admin-dashboard">
      <header className="border-b border-brand-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 bg-brand-accent" />
            <span className="font-display font-black tracking-tight">CAR SPA</span>
            <span className="text-brand-muted text-sm hidden sm:inline">· CARSPA CLUB administrace</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-brand-muted hidden md:inline" data-testid="admin-user-name">{user.name || user.username}</span>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-preview`}
              target="_blank"
              rel="noreferrer"
              data-testid="admin-email-preview"
              className="h-9 px-3 border border-brand-border hover:border-brand-accent hover:text-brand-accent transition-colors text-xs inline-flex items-center gap-2"
            >
              Náhled e-mailu
            </a>
            <button
              onClick={testEmail}
              disabled={sendingTest}
              data-testid="admin-test-email"
              className="h-9 px-3 border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-black transition-colors text-xs inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Mail size={13} /> {sendingTest ? "Odesílám..." : "Poslat test"}
            </button>
            <button
              onClick={load}
              disabled={loading}
              data-testid="admin-refresh"
              className="h-9 px-3 border border-brand-border hover:border-brand-accent hover:text-brand-accent transition-colors text-xs inline-flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Obnovit
            </button>
            <button
              onClick={async () => {
                await logout();
                setUser(false);
              }}
              data-testid="admin-logout"
              className="h-9 px-3 border border-brand-border hover:border-red-400 hover:text-red-400 transition-colors text-xs inline-flex items-center gap-2"
            >
              <LogOut size={13} /> Odhlásit
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 space-y-8">
        <div className="flex gap-1 border-b border-brand-border" data-testid="admin-tabs">
          {[["reservations", "Poptávky a tržby"], ["club", "CARSPA CLUB"], ["programs", "Akční program"], ["partners", "Partneři"], ["campaign", "Kampaň · pozvánka"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`px-4 h-11 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === k ? "border-brand-accent text-white" : "border-transparent text-brand-muted hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "campaign" && <CampaignPanel />}
        {tab === "club" && <ClubPanel scanCode={scanCode} onScanConsumed={() => { setScanCode(""); if (scanParam) navigate("/admin", { replace: true }); }} />}
        {tab === "programs" && <ProgramsPanel />}
        {tab === "partners" && <PartnersPanel />}

        {tab === "reservations" && (
          <>
            <CapacityCard refreshKey={stats} />
            <AdminStats stats={stats} />
          </>
        )}

        {tab === "reservations" && <section className="space-y-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="font-display font-black text-xl">
              Rezervace <span className="text-brand-muted font-normal text-base">({visible.length})</span>
            </h2>
            <div className="flex gap-1" data-testid="admin-status-filter">
              {[["all", "Vše"], ...Object.entries(STATUS).map(([k, v]) => [k, v.label])].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  data-testid={`filter-${k}`}
                  className={`h-8 px-3 text-xs border transition-colors ${
                    filter === k ? "bg-brand-accent text-black border-brand-accent" : "border-brand-border text-brand-muted hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <ReservationsTable items={visible} onStatus={changeStatus} />
        </section>}
      </main>
    </div>
  );
}
